import { BreadcrumbList } from '@/components/Breadcrumbs/BreadcrumbList';
import { BreadcrumbItem } from '@/components/Breadcrumbs/BreadcrumbItem';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PageHeader } from '@/components/Header/Page/PageHeader';
import { useNavigate, useParams } from 'react-router-dom';
import Loader from '@/components/Loading/Loader';
import { NotFound } from '@/components/Message/NotFound';
import { DamageInfosManagement } from '../components/DamageInfosManagement';
import { TDamageSchema, damageSchema } from '@/schemas/damage/damage.schema';
import { useGetDamageById, useUpdateDamage } from '@/hooks/damage.hooks';
import { useTranslation } from 'react-i18next';
import { getLanguage, getTranslatedItem } from '@/utils/language.utils';

export const EditDamagePage = () => {
  const { id } = useParams();
  const { data, isLoading } = useGetDamageById(`${id}`);
  const { t } = useTranslation('damage');
  const navigate = useNavigate();

  const translatedDamage = getTranslatedItem(data?.data?.translatedDamages);

  const {
    watch: watchDamage,
    control: damageControl,
    register: registerDamage,
    setValue: setDamageValue,
    setError: setDamageError,
    handleSubmit: handleSubmitDamage,
    formState: { errors: damageErrors, isSubmitting: isSubmittingDamage },
  } = useForm<TDamageSchema>({
    resolver: zodResolver(damageSchema),
  });

  const { mutate: mutateUpdateDamage, isPending: isPendingUpdateDamage } =
    useUpdateDamage({
      t,
      handleSuccess: () => navigate('/damages'),
    });

  const onSubmitDamageInfos = (damage: TDamageSchema) => {
    const { languageCode } = getLanguage();

    const dataWithLanguageCode = {
      ...damage,
      languageCode,
    };

    const damageWithId = { id, ...dataWithLanguageCode };
    mutateUpdateDamage(damageWithId);
  };

  return (
    <>
      <PageHeader
        backToLink="/damages"
        title={t('theDamage')}
        subTitle={translatedDamage?.name || data?.data?.name}
        subTitleIconClassName="ki-bucket"
        headingElement={
          <BreadcrumbList>
            <BreadcrumbItem text={t('damageList')} linkTo="/damages" />
            <BreadcrumbItem
              text={translatedDamage?.name || data?.data?.name}
              active
            />
          </BreadcrumbList>
        }
      />
      {!isLoading && !data?.data ? (
        <NotFound text={`${t('damageNotFound')}...`} />
      ) : (
        <div>
          <DamageInfosManagement<TDamageSchema>
            t={t}
            data={!isLoading && data?.data}
            watch={watchDamage}
            control={damageControl}
            register={registerDamage}
            setValue={setDamageValue}
            setError={setDamageError}
            errors={damageErrors}
            submitDisabled={isSubmittingDamage || isPendingUpdateDamage}
            onSubmit={handleSubmitDamage(onSubmitDamageInfos)}
          />
        </div>
      )}
      {(isLoading || isSubmittingDamage || isPendingUpdateDamage) && <Loader />}
    </>
  );
};
